import { computed, action } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IDateSelectionConfiguration } from '../models';
import { IDateSelectionProps } from '@kurtosys/ksys-app-components/dist/components/base/DateSelection/models';
import { LineChartStore } from '../../LineChart/stores/LineChartStore';
import { utils } from '@kurtosys/ksys-app-template/dist';
import { DaysOfWeek, printISODate } from '@kurtosys/ksys-app-components/dist/components/base/DatePicker/DatePickerUtils';
import isoAdapter from '@kurtosys/ksys-app-components/dist/components/base/DatePicker/DateAdapter';
import { IDatePickerProps } from '@kurtosys/ksys-app-components/dist/components/base/DatePicker/models';
import { IDatePickerLocalizedText } from '@kurtosys/ksys-app-components/dist/components/base/DatePicker/models/IDatePickerLocalizedText';
import { IFormatOptions } from '../../../models/commonTypes';

export class DateSelectionStore extends StoreBase {
	static componentKey: 'dateSelection' = 'dateSelection';

	@computed
	get configuration(): IDateSelectionConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(
				DateSelectionStore.componentKey,
			);
		}
	}

	@action
	async initialize(): Promise<void> { }

	@computed
	get lineChartStore(): LineChartStore {
		return this.storeContext && this.storeContext.lineChartStore;
	}

	@action
	handleDateChange = (startDate: string, endDate: string) => {
		const { appStore, lineChartStore } = this.storeContext;
		if (lineChartStore) {
			lineChartStore.selectDateRange(startDate, endDate);

			const { analyticsHelper } = appStore;
			if (analyticsHelper) {
				// Once the selectDateRange has done its deed,
				// we can pull the start and end dates off of the periodData as it will filter the data set,
				// this will align the logging dates with the data points in the DB for easier correlation
				const periodData = this.lineChartStore.periodData;
				let dataStartDate;
				let dataEndDate;
				if (periodData && utils.isArrayLike.isArrayLike(periodData)) {
					dataStartDate = periodData[0].date;
					dataEndDate = periodData[periodData.length - 1].date;
				}
				else {
					console.warn(
						`Chart Period Selection Store: handleOnClick, could not pull the data start and data end dates from ${ periodData }. Expecting an array`,
					);
				}
				analyticsHelper.logEvent({
					event: 'date_selection_click',
					eventType: 'click',
					context: {
						startDate: dataStartDate,
						endDate: dataEndDate,
					},
				});
			}
		}
	}

	@computed
	get baseDatePickerProps(): Partial<IDatePickerProps> {
		const baseProps: Partial<IDatePickerProps> = {
			disabled: false,
			direction: 'right',
			required: false,
			min: this.minAllowedValue,
			max: this.maxAllowedValue,
			firstDayOfWeek: DaysOfWeek.Sunday,
			localization: this.datePickerLocalization,
		};

		return baseProps;
	}

	formatValue = (value: string, options?: IFormatOptions): string => {
		const { translationStore } = this.storeContext;
		if (options) {
			return utils.formatter.formatDateTime(translationStore.culture, options, value, translationStore.translate);
		}
		return value;
	}

	@computed
	get datePickerLocalization(): IDatePickerLocalizedText {
		const localization: IDatePickerLocalizedText = {
			buttonLabel: 'Choose date',
			placeholder: 'YYYY-MM-DD',
			selectedDateMessage: 'Selected date is',
			prevMonthLabel: 'Previous month',
			nextMonthLabel: 'Next month',
			monthSelectLabel: 'Month',
			yearSelectLabel: 'Year',
			closeLabel: 'Close window',
			calendarHeading: 'Choose a date',
			dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
			monthNames: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			],
			monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
			locale: 'en-GB',
		};
		return localization;
	}

	@computed
	get allowedValues(): number[] {
		const { data } = this.lineChartStore;
		return data.map(d => d.dateAsTimestamp);
	}

	@computed
	get minAllowedValue(): string {
		const minDate = Math.min(...this.allowedValues);
		return printISODate(new Date(minDate));
	}

	@computed
	get maxAllowedValue(): string {
		const maxDate = Math.max(...this.allowedValues);
		return printISODate(new Date(maxDate));
	}


	@computed
	get dateSelectionProps(): IDateSelectionProps | undefined {
		const dateSelectionConfig = this.configuration;
		if (dateSelectionConfig) {
			const { periodData, data } = this.lineChartStore;
			const { calendarStartDateProps, calendarEndDateProps } = dateSelectionConfig;
			if (periodData && periodData.length > 0) {
				const startDate = periodData[0].date;
				const endDate = periodData[periodData.length - 1].date;
				const response: IDateSelectionProps = {
					formatOverride: dateSelectionConfig.formatOverride,
					endDateProps: {
						...dateSelectionConfig.endDateProps,
						value: endDate.toString(),
					},
					mode: dateSelectionConfig.mode,
					startDateProps: {
						...dateSelectionConfig.startDateProps,
						value: startDate.toString(),
					},
					calendarStartDateProps: {
						name: 'ksys-start-date-picker',
						...this.baseDatePickerProps,
						...calendarStartDateProps,
						localization: this.datePickerLocalization,
						value: startDate.toString(),
						dateAdapter: {
							...isoAdapter,
							format: (date: Date) => {
								const isoDateString = printISODate(date);
								if (calendarStartDateProps && calendarStartDateProps.formatOptions) {
									return this.formatValue(isoDateString, calendarStartDateProps.formatOptions);
								}
								return isoDateString;
							},
						},
					},
					calendarEndDateProps: {
						name: 'ksys-end-date-picker',
						...this.baseDatePickerProps,
						...calendarEndDateProps,
						localization: this.datePickerLocalization,
						value: endDate.toString(),
						dateAdapter: {
							...isoAdapter,
							format: (date: Date) => {
								const isoDateString = printISODate(date);
								if (calendarEndDateProps && calendarEndDateProps.formatOptions) {
									return this.formatValue(isoDateString, calendarEndDateProps.formatOptions);
								}
								return isoDateString;
							},
						},
					},
					dateRange: data.map(d => d.date),
					onChange: this.handleDateChange,
				};
				return response;
			}
		}
		return undefined;
	}
}
